import { useMemo, useState } from "react";
import { Check, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toggleSelectedValue } from "@/lib/listings-search-params";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  BodySelectOption,
  MakeSelectOption,
  matchesVehicleOptionQuery,
  ModelSelectOption,
  vehicleOptionTextValue,
} from "@/components/add-car/vehicle-select-option";

const NONE_VALUE = "__none__";

export type VehicleSearchOption = {
  value: string;
  nameAr: string;
  nameEn: string;
  logo?: string | null;
  image?: string;
  imageWhiteBg?: boolean;
  imageMirror?: boolean;
};

type Props = {
  id: string;
  value?: string;
  onValueChange?: (value: string) => void;
  multiple?: boolean;
  values?: string[];
  onValuesChange?: (values: string[]) => void;
  options: VehicleSearchOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyText?: string;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  variant: "make" | "model" | "body";
  triggerClassName?: string;
  noneOption?: { nameAr: string; nameEn?: string };
};

function OptionRow({
  option,
  variant,
  className,
}: {
  option: VehicleSearchOption;
  variant: Props["variant"];
  className?: string;
}) {
  if (variant === "make") {
    return (
      <MakeSelectOption
        className={className}
        nameAr={option.nameAr}
        nameEn={option.nameEn}
        logo={option.logo}
      />
    );
  }
  if (variant === "body" && option.image) {
    return (
      <BodySelectOption
        className={className}
        nameAr={option.nameAr}
        nameEn={option.nameEn}
        image={option.image}
        imageWhiteBg={option.imageWhiteBg}
        imageMirror={option.imageMirror}
      />
    );
  }
  return (
    <ModelSelectOption className={className} nameAr={option.nameAr} nameEn={option.nameEn} />
  );
}

export function VehicleSearchSelect({
  id,
  value,
  onValueChange,
  multiple = false,
  values,
  onValuesChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyText = "لا توجد نتائج",
  disabled = false,
  loading = false,
  loadingText = "جاري التحميل...",
  variant,
  triggerClassName,
  noneOption,
}: Props) {
  const [open, setOpen] = useState(false);
  const selectedValues = useMemo(
    () => (multiple ? values ?? [] : value ? [value] : []),
    [multiple, value, values],
  );
  const selectedOptions = useMemo(
    () => options.filter((option) => selectedValues.includes(option.value)),
    [options, selectedValues],
  );
  const selected = selectedOptions[0];
  const noneSelected = Boolean(noneOption) && selectedOptions.length === 0;
  const noneNameEn = noneOption?.nameEn ?? "";

  const triggerLabel =
    selectedOptions.length === 0
      ? null
      : selectedOptions.length === 1 && selected
        ? <OptionRow variant={variant} option={selected} className="flex-1" />
        : (
          <span className="flex-1 truncate text-right">
            {selectedOptions.map((option) => option.nameAr).join("، ")}
          </span>
        );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (disabled) return;
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          aria-multiselectable={multiple || undefined}
          aria-controls={`${id}-listbox`}
          dir="rtl"
          data-size="default"
          className={cn(
            "border-input [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-[#0071BC] focus-visible:ring-[#0071BC]/20 dark:bg-input/30 dark:hover:bg-input/50 flex w-full min-w-0 flex-row-reverse items-center justify-between gap-2 rounded-xl border bg-white px-3 py-2 text-sm text-right shadow-sm transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-10",
            selectedOptions.length === 0 && !noneOption && "text-muted-foreground",
            triggerClassName,
          )}
        >
          {triggerLabel ? (
            triggerLabel
          ) : noneOption ? (
            <ModelSelectOption
              className="flex-1"
              nameAr={noneOption.nameAr}
              nameEn={noneNameEn}
            />
          ) : (
            <span className="flex-1 truncate text-right">{placeholder}</span>
          )}
          <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] p-0"
        dir="rtl"
      >
        <Command
          filter={(itemValue, search, keywords) => {
            const nameAr = keywords?.[0] ?? "";
            const nameEn = keywords?.[1] ?? itemValue;
            return matchesVehicleOptionQuery(nameAr, nameEn, search) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} className="text-right" autoFocus />
          <CommandList id={`${id}-listbox`}>
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                {loadingText}
              </div>
            ) : (
              <>
                <CommandEmpty>{emptyText}</CommandEmpty>
                <CommandGroup>
                  {noneOption ? (
                    <CommandItem
                      value={`${NONE_VALUE} ${vehicleOptionTextValue(noneOption.nameAr, noneNameEn)}`}
                      keywords={[noneOption.nameAr, noneNameEn]}
                      className="py-3 data-[selected=true]:text-accent-foreground"
                      onSelect={() => {
                        if (multiple) onValuesChange?.([]);
                        else onValueChange?.("");
                        setOpen(false);
                      }}
                    >
                      <ModelSelectOption
                        className="flex-1"
                        nameAr={noneOption.nameAr}
                        nameEn={noneNameEn}
                      />
                      <Check
                        className={cn("size-4 shrink-0", noneSelected ? "opacity-100" : "opacity-0")}
                        aria-hidden
                      />
                    </CommandItem>
                  ) : null}
                  {options.map((option) => {
                    const selectedItem = selectedValues.includes(option.value);
                    return (
                      <CommandItem
                        key={option.value}
                        value={`${option.value} ${vehicleOptionTextValue(option.nameAr, option.nameEn)}`}
                        keywords={[option.nameAr, option.nameEn]}
                        className="py-3 data-[selected=true]:text-accent-foreground"
                        onSelect={() => {
                          if (multiple) {
                            onValuesChange?.(toggleSelectedValue(selectedValues, option.value));
                            return;
                          }
                          onValueChange?.(option.value);
                          setOpen(false);
                        }}
                      >
                        <OptionRow variant={variant} option={option} className="flex-1" />
                        <Check
                          className={cn(
                            "size-4 shrink-0",
                            selectedItem ? "opacity-100" : "opacity-0",
                          )}
                          aria-hidden
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
